import { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFloppyDisk,
  faLayerGroup,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import { APISD } from "../../../../../../../../config/apisd";
import ToastError from "../../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../../NotificationToast/ToastSuccess";

import "bulma/css/bulma.css";

const modalStyles = `
  .predikat-modal .modal-content {
    overflow: hidden;
    border: 0;
    border-radius: 16px;
    box-shadow: 0 24px 70px rgba(31, 48, 86, 0.2);
  }

  .predikat-modal-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 20px;
    border-bottom: 1px solid #edf0f5;
    background: #ffffff;
  }

  .predikat-modal-icon {
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 11px;
    background: #edf4ff;
    color: #315fba;
  }

  .predikat-modal-title {
    margin: 0;
    color: #202b43;
    font-size: 16px;
    font-weight: 800;
  }

  .predikat-modal-subtitle {
    margin: 2px 0 0;
    color: #8a96aa;
    font-size: 12px;
  }

  .predikat-modal-close {
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    border: 1px solid #e2e7ef;
    border-radius: 9px;
    background: #ffffff;
    color: #78859a;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .predikat-modal-close:hover:not(:disabled) {
    background: #fff2f2;
    color: #d84a4a;
  }

  .predikat-modal-close:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .predikat-modal-body {
    padding: 20px !important;
  }

  .predikat-context-card {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 14px;
    margin-bottom: 18px;
    padding: 13px 14px;
    border: 1px solid #dce8ff;
    border-radius: 11px;
    background: #f7faff;
  }

  .predikat-context-label {
    display: block;
    margin-bottom: 3px;
    color: #8a96aa;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .predikat-context-value {
    display: block;
    color: #3b4860;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
    word-break: break-word;
  }

  .predikat-form-group {
    margin-bottom: 16px;
  }

  .predikat-form-label {
    display: block;
    margin-bottom: 7px;
    color: #465269;
    font-size: 12px;
    font-weight: 700;
  }

  .predikat-required {
    color: #dc4c4c;
  }

  .predikat-form-control {
    width: 100%;
    min-height: 42px;
    padding: 10px 12px;
    border: 1px solid #dce3ef;
    border-radius: 9px;
    outline: none;
    background: #ffffff;
    color: #28344b;
    font-size: 13px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .predikat-form-control:focus {
    border-color: #4c78d3;
    box-shadow: 0 0 0 3px rgba(76, 120, 211, 0.12);
  }

  textarea.predikat-form-control {
    min-height: 96px;
    resize: vertical;
  }

  .predikat-form-hint {
    margin: 5px 0 0;
    color: #98a2b2;
    font-size: 11px;
  }

  .predikat-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 9px;
    padding: 15px 20px;
    border-top: 1px solid #edf0f5;
    background: #fafbfd;
  }

  .predikat-modal-button {
    min-height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 16px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.15s ease, background 0.2s ease;
  }

  .predikat-modal-button:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  .predikat-modal-button:disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }

  .predikat-button-cancel {
    border: 1px solid #dfe5ee;
    background: #ffffff;
    color: #66738a;
  }

  .predikat-button-submit {
    border: 1px solid #3768c5;
    background: #3768c5;
    color: #ffffff;
    box-shadow: 0 5px 14px rgba(55, 104, 197, 0.2);
  }

  .predikat-button-submit:hover:not(:disabled) {
    background: #2f59a9;
  }

  @media (max-width: 576px) {
    .predikat-context-card {
      grid-template-columns: 1fr;
    }

    .predikat-modal-footer {
      flex-direction: column-reverse;
    }

    .predikat-modal-button {
      width: 100%;
    }
  }
`;

const getUserId = () =>
  (
    localStorage.getItem("id_admin") ||
    localStorage.getItem("user_id") ||
    localStorage.getItem("id_user") ||
    ""
  ).replace(/[|&;$%@"<>()+,]/g, "");

const safeValue = (value) => (value === null || value === undefined ? "" : value);

export default function ModalPredikatUpdate(props) {
  const token = localStorage.getItem("token");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    predikat: "",
    description: "",
  });

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  useEffect(() => {
    if (!props.show) return;

    setForm({
      predikat: String(safeValue(props?.dataUpdate?.predikat)),
      description: String(safeValue(props?.dataUpdate?.description)),
    });
  }, [props.show, props.dataUpdate]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "predikat") {
      const cleanValue = value.replace(/[^0-9]/g, "").slice(0, 3);
      setForm((previous) => ({ ...previous, predikat: cleanValue }));
      return;
    }

    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const row = props?.dataUpdate || {};
    const filterData = props?.filterData || {};

    const payloadContext = {
      ysb_academic_year_id:
        row?.ysb_academic_year_id || filterData.ysb_academic_year_id || "",
      ysb_semester_id: row?.ysb_semester_id || filterData.ysb_semester_id || "",
      ysb_branch_id: row?.ysb_branch_id || filterData.ysb_branch_id || "",
      ysb_school_id: row?.ysb_school_id || filterData.ysb_school_id || "",
      ysb_grade_level_id:
        row?.ysb_grade_level_id || filterData.ysb_grade_level_id || "",
    };

    if (Object.values(payloadContext).some((value) => !value)) {
      ToastError.fire({
        icon: "error",
        title: "Data filter belum lengkap",
      });
      return;
    }

    if (form.predikat === "" || Number(form.predikat) > 100) {
      ToastError.fire({
        icon: "error",
        title: "Nilai predikat harus berada di antara 0 sampai 100",
      });
      return;
    }

    if (!form.description.trim()) {
      ToastError.fire({
        icon: "error",
        title: "Keterangan predikat wajib diisi",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await APISD.put(
        `/api/ekstrakurikuler-predikat/${props.id}`,
        {
          ...payloadContext,
          predikat: Number(form.predikat),
          description: form.description.trim(),
          user_id: getUserId(),
        },
        fetchParams
      );

      if (response?.status === 200 || response?.status === 201) {
        ToastSuccess.fire({
          icon: "success",
          title: response?.data?.message || "Predikat berhasil diperbarui",
        });

        await props.GetResponseData?.();
        props.onHide?.();
      }
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message || "Gagal memperbarui predikat",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{modalStyles}</style>
      <Modal
        {...props}
        className="predikat-modal"
        size="md"
        centered
        backdrop="static"
        keyboard={false}
      >
        <div className="predikat-modal-header">
          <span className="predikat-modal-icon">
            <FontAwesomeIcon icon={faLayerGroup} />
          </span>
          <div>
            <h5 className="predikat-modal-title">Update Predikat</h5>
            {/* <p className="predikat-modal-subtitle">
              Perbarui nilai dan keterangan predikat ekstrakurikuler.
            </p> */}
          </div>
          <button
            type="button"
            className="predikat-modal-close"
            onClick={props.onHide}
            disabled={isSubmitting}
            aria-label="Tutup modal"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <Modal.Body className="predikat-modal-body mt-1 mx-2">
            <div className="predikat-context-card">
              <div>
                <span className="predikat-context-label">Tahun Ajaran</span>
                <span className="predikat-context-value">
                  {props?.filterLabels?.academicYear || "-"}
                </span>
              </div>
              <div>
                <span className="predikat-context-label">Cabang</span>
                <span className="predikat-context-value">
                  {props?.filterLabels?.branch || "-"}
                </span>
              </div>
              <div>
                <span className="predikat-context-label">Sekolah</span>
                <span className="predikat-context-value">
                  {props?.filterLabels?.school || "-"}
                </span>
              </div>
              <div>
                <span className="predikat-context-label">Tingkatan</span>
                <span className="predikat-context-value">
                  {props?.filterLabels?.gradeLevel || "-"}
                </span>
              </div>
            </div>

            <div className="predikat-form-group">
              <label className="predikat-form-label" htmlFor="update-predikat">
                Nilai Predikat <span className="predikat-required">*</span>
              </label>
              <input
                id="update-predikat"
                className="predikat-form-control"
                type="text"
                inputMode="numeric"
                name="predikat"
                value={form.predikat}
                onChange={handleChange}
                autoFocus
                required
              />
              {/* <p className="predikat-form-hint">Masukkan nilai 0 sampai 100.</p> */}
            </div>

            <div className="predikat-form-group">
              <label
                className="predikat-form-label"
                htmlFor="update-description"
              >
                Keterangan Predikat <span className="predikat-required">*</span>
              </label>
              <textarea
                id="update-description"
                className="predikat-form-control"
                name="description"
                value={form.description}
                onChange={handleChange}
                required
              />
            </div>
          </Modal.Body>

          <div className="predikat-modal-footer">
            <button
              type="button"
              className="predikat-modal-button predikat-button-cancel"
              onClick={props.onHide}
              disabled={isSubmitting}
            >
              Batal
            </button>
            <button
              type="submit"
              className="predikat-modal-button predikat-button-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" />
                  <span>Memperbarui...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFloppyDisk} />
                  <span>Update Predikat</span>
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
